import { useEffect } from 'react';
import { useKoobSidebarCollapsed } from '@koob/ui';
import ComposeScreen from './ComposeScreen';
import { useLocation, useNavigate } from 'react-router-dom';
import { useExperienceBooking } from '../../../hooks';
import Spinner from '../../../components/Spinner';
import KoobLayout from '../../../components/Koob/KoobLayout';
import {
  useTripStore,
  useTripFilters,
  useLoading,
  useSetTripFilters,
} from '@koob/trip-designer';
import { openTripWizard } from '@koob/ui/src/features/tripWizard';
import { useBindActiveFolder } from './hooks/useBindActiveFolder';

export default function TripComposer() {
  const navigate = useNavigate();
  const { state } = useLocation();
  useBindActiveFolder();
  const tripFilters = useTripFilters();
  const loading = useLoading();
  const setTripFilters = useSetTripFilters();
  const { resetBasket } = useExperienceBooking();
  const [, setSidebarCollapsed] = useKoobSidebarCollapsed();

  // KD-3391: the composer needs the full width — collapse the sidebar while
  // on the page and restore the user's previous state on exit.
  useEffect(() => {
    const wasCollapsed =
      localStorage.getItem('koob-sidebar-collapsed') === 'true';
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(wasCollapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The store is loaded from the API by ComposeScreen.js (getTrip +
  // toTripState) or, before navigating here, by useHydrateTrip. It is
  // never seeded from the cached `tripData`: that copy could be stale or, in
  // tests, unformatted, and it raced the API load.
  const days = useTripStore(state => state.days);

  useEffect(() => {
    if (!tripFilters) {
      openTripWizard();
    }
    resetBasket([]);
  }, []);

  // Sync days.length to tripFilters.tripDuration so endDate calculation stays accurate
  // Only increase tripDuration to match days length to avoid overwriting a user's explicit update
  useEffect(() => {
    if (days.length > 0 && days.length > (tripFilters?.tripDuration ?? 0)) {
      setTripFilters(current => ({
        ...current,
        tripDuration: days.length,
      }));
    }
  }, [days.length, tripFilters?.tripDuration, setTripFilters]);

  useEffect(() => {
    if (loading) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [loading]);

  return tripFilters ? (
    <KoobLayout variant="tripDesigner">
      {loading && (
        <div className="absolute z-modal-backdrop top-0 left-0 h-screen w-screen bg-black opacity-50">
          <Spinner />
        </div>
      )}

      <ComposeScreen tripId={state?.tripId ?? tripFilters?.tripId} />
    </KoobLayout>
  ) : null;
}
